import React from "react";
import StickyBox from "react-sticky-box";

import { cn } from "@/utils/cn";

interface SidebarProps {
  children: React.ReactNode;
}
interface AsideProps {
  children?: React.ReactNode;
}
interface LayoutProps {
  children: React.ReactNode;
}

interface LayoutComponent extends React.FC<LayoutProps> {
  Sidebar: React.FC<SidebarProps>;
  Main: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  Aside: React.FC<AsideProps>;
}

/** Layout container, to hold Sidebar, Main, and Aside. */
const Layout: LayoutComponent = ({ children }) => (
  <div className="min-h-screen bg-white dark:bg-gray-900">
    <div className="relative mx-auto max-w-3xl md:grid md:max-w-7xl md:grid-cols-12 md:gap-8 lg:px-8">
      {children}
    </div>
  </div>
);

/** Left sidebar container in the UI. */
const Sidebar: React.FC<SidebarProps> = ({ children }) => (
  <aside className="sticky top-0 hidden h-screen self-start lg:col-span-3 lg:block lg:pt-4 xl:col-span-3">
    {children}
  </aside>
);

/** Center column container in the UI. */
const Main: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => (
  <main
    {...props}
    className={cn(
      "md:col-span-12 lg:col-span-9 xl:col-span-6",
      "lg:border-x",
      className,
    )}
  >
    {children}
  </main>
);

/** Right sidebar container in the UI. */
const Aside: React.FC<AsideProps> = ({ children }) => (
  <aside className="sticky top-0 hidden xl:col-span-3 xl:block xl:pt-4">
    <StickyBox offsetTop={16} className="space-y-3 pb-16">
      {children}
    </StickyBox>
  </aside>
);

Layout.Sidebar = Sidebar;
Layout.Main = Main;
Layout.Aside = Aside;

export default Layout;
